import { Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Box, Button, Divider, Flex, FormControl, Input, InputGroup, InputRightAddon, Link, Stack, Text, useToast } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { fetchCompany, updateGoogleApiKey, updateSecretKey } from '../../../store/actions';
import { useAppDispatch, useAppSelector, useCopyToClipboard } from '../../../hooks';

import { AppDispatch } from '../../../store';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { HiOutlineClipboardCopy } from 'react-icons/hi';

export const AccessKeysView = () => {

    const toast = useToast();
    //eslint-disable-next-line
    const [value, copy] = useCopyToClipboard();

    const [googleApiKey, setGoogleApiKey] = useState<string>('');
    const [isUpdatingGoogleApiKey, setUpdatingGoogleApiKey] = useState<boolean>(false);
    const [isUpdatingSecretKey, setUpdatingSecretKey] = useState<boolean>(false);

    const { company } = useAppSelector(state => state.companyReducer);
    const { google_api_key, company_id, secret_key, application_key } = company;

    const dispatch: AppDispatch = useAppDispatch();

    const handleClipboardCopy = (keyType: string, value: string) => {
        if (value) {
            copy(value);
            toast({
                title: 'Copied.',
                description: `${keyType} is copied.`,
                status: 'success',
                position: 'top-right',
                duration: 9000,
                isClosable: true,
            });
        } else {
            console.error('No secret key copied!');
        }

    };

    const handleGenerateSecretKey = async () => {
        setUpdatingSecretKey(true);
        dispatch(updateSecretKey({
            company_id,
        }))
            .then(() => {
                setUpdatingSecretKey(false);
                dispatch(fetchCompany());
            })
            .catch(() => setUpdatingSecretKey(false));
    };

    const handleUpdateGoogleApiKey = () => {
        setUpdatingGoogleApiKey(true);
        dispatch(updateGoogleApiKey({
            company_id,
            google_api_key: googleApiKey
        }))
            .then(() => {
                setUpdatingGoogleApiKey(false);
                dispatch(fetchCompany());
            })
            .catch(() => setUpdatingGoogleApiKey(false));
    };

    const setKeyValues = () => {
        setGoogleApiKey(google_api_key ?? '');
    };

    useEffect(() => {
        setKeyValues();
    }, []);

    return (
        <>
            <Flex>
                <Stack bg={'#FFFFFF'} p={4} borderRadius={6} w={'full'}>
                    <Text fontSize={'xl'} fontWeight={'medium'}>Access Keys.</Text>

                    <Divider />

                    <Accordion defaultIndex={[0, 1, 2]} allowMultiple allowToggle={false}>
                        <AccordionItem boxShadow={'md'} borderRadius={4} border={'none'}>
                            <h2>
                                <AccordionButton bg={'gray.50'} minH={'50px'}>
                                    <Box as="span" flex='1' textAlign='left' fontWeight={'medium'}>
                                        Application key
                                    </Box>
                                    <AccordionIcon />
                                </AccordionButton>
                            </h2>
                            <AccordionPanel pb={4}>
                                <FormControl isReadOnly>
                                    <InputGroup>
                                        <Input placeholder=''
                                            value={application_key ?? ''}
                                            _focusVisible={{
                                                boxShadow: '0 0 0 1px #ffc107'
                                            }}
                                        />
                                        <InputRightAddon
                                            onClick={() => handleClipboardCopy('Application key', application_key)}
                                        >
                                            <HiOutlineClipboardCopy />
                                        </InputRightAddon>
                                    </InputGroup>
                                </FormControl>
                            </AccordionPanel>
                        </AccordionItem>

                        <AccordionItem boxShadow={'md'} borderRadius={4} border={'none'} mt={6}>
                            <h2>
                                <AccordionButton bg={'gray.50'} minH={'50px'}>
                                    <Box as="span" flex='1' textAlign='left' fontWeight={'medium'}>
                                        Secret key
                                    </Box>
                                    <AccordionIcon />
                                </AccordionButton>
                            </h2>
                            <AccordionPanel pb={4}>
                                <FormControl isReadOnly>
                                    <InputGroup>
                                        <Input
                                            placeholder=''
                                            value={secret_key ?? ''}
                                            _focusVisible={{
                                                boxShadow: '0 0 0 1px #ffc107'
                                            }}
                                        />
                                        <InputRightAddon
                                            onClick={() => handleClipboardCopy('Secret key', secret_key)}
                                        >
                                            <HiOutlineClipboardCopy />
                                        </InputRightAddon>
                                    </InputGroup>
                                </FormControl>
                                <Button
                                    my={3}
                                    float={'right'}
                                    colorScheme={'yellow'}
                                    disabled={isUpdatingSecretKey}
                                    isLoading={isUpdatingSecretKey}
                                    loadingText={'Generating...'}
                                    onClick={handleGenerateSecretKey}
                                >Generate Key</Button>
                            </AccordionPanel>
                        </AccordionItem>

                        <AccordionItem boxShadow={'md'} borderRadius={4} border={'none'} mt={6}>
                            <h2>
                                <AccordionButton bg={'gray.50'} minH={'50px'} disabled>
                                    <Box as='span' flex='1' textAlign='left' fontWeight={'medium'}>
                                        Google API Key
                                    </Box>
                                    <AccordionIcon />
                                </AccordionButton>
                            </h2>
                            <AccordionPanel pb={4}>
                                <form>
                                    <FormControl>
                                        <Input placeholder='1EEA6DC-JAM4DP2-PHVYPBN-V0XCJ9X'
                                            value={googleApiKey}
                                            onChange={(event => {
                                                setGoogleApiKey(event.target.value);
                                            })}
                                            _focusVisible={{
                                                boxShadow: '0 0 0 1px #ffc107'
                                            }}
                                        />
                                        {!googleApiKey && <Text color={'red.500'} fontSize={'xs'}>Google api key is required.</Text>}
                                    </FormControl>
                                    <Stack>
                                        <Link w={'max-content'} color={'blue.500'} href='https://support.google.com/googleapi/answer/6158862?hl=en' isExternal>How to generate a google-api-key? <ExternalLinkIcon mx='2px' /></Link>
                                    </Stack>
                                    <Button
                                        my={3}
                                        float={'right'}
                                        colorScheme={'yellow'}
                                        disabled={isUpdatingGoogleApiKey}
                                        isLoading={isUpdatingGoogleApiKey}
                                        loadingText={'Updating...'}
                                        onClick={handleUpdateGoogleApiKey}
                                    >Update</Button>
                                </form>
                            </AccordionPanel>
                        </AccordionItem>
                    </Accordion>
                </Stack>
            </Flex>
        </>
    );
};